import React, { useEffect, useState } from "react";
import { MapPin, Store, Phone, Mail, ShieldAlert, Loader2, Navigation, Route } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { StatusBadge } from "./ui";
import { inr, fmtDateTime } from "../../lib/api";

function Row({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-ink-muted">{label}</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  );
}

function SupportBlock({ support, refId, subject }) {
  if (!support) return null;
  const phone = String(support.phone || "").replace(/\s+/g, "");
  const body = encodeURIComponent(`Hi, I'd like to request the customer's contact details for ${subject} ${refId}. Reason: `);
  const mail = `mailto:${support.email}?subject=${encodeURIComponent(`Customer details request — ${refId}`)}&body=${body}`;
  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="support-block">
      <p className="flex items-start gap-2 text-sm text-amber-800"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {support.note || "The customer's phone number is hidden for privacy. Contact support to request it."}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700" data-testid="support-call"><Phone className="h-3.5 w-3.5" /> Call support</a>
        <a href={mail} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100" data-testid="support-email"><Mail className="h-3.5 w-3.5" /> Email support</a>
      </div>
    </div>
  );
}

function Charges({ o }) {
  return (
    <div className="mt-3 rounded-xl border border-surface-200 p-3">
      <Row label="Subtotal" value={inr(o.subtotal)} />
      {o.packaging_fee ? <Row label="Packaging" value={inr(o.packaging_fee)} /> : null}
      {o.delivery_fee ? <Row label="Delivery fee" value={inr(o.delivery_fee)} /> : null}
      {o.tax ? <Row label="GST (5%)" value={inr(o.tax)} /> : null}
      {o.tip ? <Row label="Tip" value={inr(o.tip)} /> : null}
      {o.discount ? <Row label="Discount" value={`− ${inr(o.discount)}`} /> : null}
      <div className="mt-1 flex items-center justify-between border-t border-surface-200 pt-2 text-sm font-bold text-ink"><span>Total</span><span>{inr(o.total)}</span></div>
      <Row label="Payment" value={`${o.payment_method === "razorpay" ? "Online" : "Cash"}${o.payment_status === "refunded" ? " · refunded" : o.payment_status === "paid" ? " · paid" : ""}`} />
    </div>
  );
}

function Items({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">Items</p>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between text-sm" data-testid={`detail-item-${i}`}>
            <span className="text-ink">{it.qty} × {it.name}</span>
            <span className="text-ink-soft">{inr(it.line_total ?? (it.price || 0) * (it.qty || 1))}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Restaurant Sales Report → View Order
export function RestaurantOrderDialog({ orderId, fetcher, onClose }) {
  const [res, setRes] = useState(null);
  useEffect(() => { if (orderId) { setRes(null); fetcher(orderId).then(setRes).catch(() => setRes(false)); } else { setRes(null); } }, [orderId, fetcher]);
  const o = res?.order;
  return (
    <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="restaurant-order-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">Order #{orderId?.slice(-6).toUpperCase()}{o ? <StatusBadge status={o.status} /> : null}</DialogTitle></DialogHeader>
        {!res && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>}
        {res === false && <p className="py-6 text-center text-sm text-ink-muted">Couldn't load this order.</p>}
        {orderId && o && (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <Row label="Date" value={fmtDateTime(o.created_at)} />
            <Row label="Customer" value={o.customer_name || "—"} />
            {o.driver_name ? <Row label="Delivery partner" value={o.driver_name} /> : null}
            {o.delivery_address && <p className="mt-2 flex items-start gap-2 text-sm text-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {o.delivery_address}</p>}
            <Items items={o.items} />
            <Charges o={o} />
            {o.notes && <p className="mt-3 rounded-xl bg-brand-50 px-3 py-2 text-sm text-ink">Note: {o.notes}</p>}
            <SupportBlock support={res.support_contact} refId={`#${orderId?.slice(-6).toUpperCase() || ""}`} subject="order" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Driver History → View (ride or food delivery)
export function DriverHistoryDialog({ item, fetcher, onClose }) {
  const [res, setRes] = useState(null);
  const isRide = item?.kind === "ride";
  useEffect(() => {
    if (!item) return;
    setRes(null);
    fetcher(item.kind, item.id).then(setRes).catch(() => setRes(false));
  }, [item, fetcher]);
  const d = res?.ride || res?.order;
  const label = isRide ? "Ride" : "Delivery";
  const refShort = item ? `#${item.id.slice(-6).toUpperCase()}` : "";
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="driver-history-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{label} {refShort}{d ? <StatusBadge status={d.status} /> : null}</DialogTitle></DialogHeader>
        {!res && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>}
        {res === false && <p className="py-6 text-center text-sm text-ink-muted">Couldn't load this {label.toLowerCase()}.</p>}
        {d && (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <Row label="Date" value={fmtDateTime(d.created_at)} />
            <Row label="Customer" value={d.customer_name || "—"} />
            {isRide ? (
              <>
                <Row label="Vehicle" value={d.vehicle_type ? d.vehicle_type[0].toUpperCase() + d.vehicle_type.slice(1) : null} />
                <div className="mt-2 space-y-1.5 rounded-xl bg-surface-100 p-3 text-sm">
                  <p className="flex items-start gap-2 text-ink"><Navigation className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {d.pickup_address || "—"}</p>
                  <p className="flex items-start gap-2 text-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {d.drop_address || "—"}</p>
                  {(d.distance_km || d.duration_min) && <p className="flex items-center gap-2 text-xs text-ink-muted"><Route className="h-3.5 w-3.5" /> {d.distance_km ? `${d.distance_km} km` : ""}{d.duration_min ? ` · ${d.duration_min} min` : ""}</p>}
                </div>
                <div className="mt-3 rounded-xl border border-surface-200 p-3">
                  {d.base_fare ? <Row label="Base fare" value={inr(d.base_fare)} /> : null}
                  {d.distance_fee ? <Row label="Far-pickup fee" value={inr(d.distance_fee)} /> : null}
                  {d.waiting_charge ? <Row label={`Waiting (${d.waiting_min || 0} min)`} value={inr(d.waiting_charge)} /> : null}
                  {d.discount ? <Row label="Discount" value={`− ${inr(d.discount)}`} /> : null}
                  <div className="mt-1 flex items-center justify-between border-t border-surface-200 pt-2 text-sm font-bold text-ink"><span>Fare</span><span>{inr(d.fare)}</span></div>
                  {d.platform_fee ? <Row label="Platform fee" value={`− ${inr(d.platform_fee)}`} /> : null}
                  {d.net_earning != null ? <div className="flex items-center justify-between text-sm font-semibold text-emerald-700"><span>Your earning</span><span>{inr(d.net_earning)}</span></div> : null}
                  <Row label="Payment" value={`${d.payment_method === "razorpay" ? "Online" : "Cash"}${d.payment_status === "paid" ? " · paid" : ""}`} />
                </div>
              </>
            ) : (
              <>
                <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-ink"><Store className="h-4 w-4 text-brand" /> {d.restaurant_name}</p>
                {d.delivery_address && <p className="mt-2 flex items-start gap-2 text-sm text-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {d.delivery_address}</p>}
                <Items items={d.items} />
                <Charges o={d} />
              </>
            )}
            <SupportBlock support={res.support_contact} refId={refShort} subject={isRide ? "ride" : "order"} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
